import {i18n} from "@/i18n";
import * as Sentry from "@sentry/react";
import {AxiosError} from "axios";
import {snakeCase} from "lodash";
import {toast} from "react-toastify";

export type TError = AxiosError<{detail?: string | string[]} & Record<string, string[]>>;

const getTranslation = ({
  message,
  count,
  defaultMessage,
}: {
  message: string;
  count?: number;
  defaultMessage?: string;
}) => {
  const fallbackMessage = defaultMessage ?? i18n.t("errors:toast_global_error");
  const translationKey = `errors:${snakeCase(message)}`;
  const translatedMessage = i18n.t(translationKey, {count});

  if (translationKey === `errors:${translatedMessage}`) {
    return fallbackMessage;
  }

  return translatedMessage;
};

const captureErrorInSentry = (error: TError) => {
  const ERROR_KEYS = ["picture", "detail"] as const;

  ERROR_KEYS.map(key => {
    const messages = error.response?.data?.[key];

    if (messages) {
      (messages instanceof Array ? messages : [messages]).map(message =>
        Sentry.captureException(new AxiosError(message)),
      );
    }
  });
};

export const getResponseErrorMessages = (error: unknown): string[] => {
  const data = (error as TError)?.response?.data;
  const genericMessage = i18n.t("errors:toast_global_error");

  if (!data) {
    return [genericMessage];
  }

  const rawMessages =
    typeof data === "string"
      ? [data]
      : Object.values(data).flatMap(value => {
          if (typeof value === "string") {
            return [value];
          }
          return Array.isArray(value) ? value.filter(entry => typeof entry === "string") : [];
        });

  if (rawMessages.length === 0) {
    return [genericMessage];
  }

  return rawMessages.map(message => getTranslation({message, defaultMessage: message}));
};

export const handleErrorWithToast = ({
  error,
  count,
  defaultMessage,
}: {
  error: TError;
  count?: number;
  defaultMessage?: string;
}) => {
  const ERROR_KEYS = ["picture", "detail"] as const;
  const fallbackMessage = defaultMessage ?? i18n.t("errors:toast_global_error");

  const allMessages = ERROR_KEYS.flatMap(key => {
    const messages = error.response?.data?.[key];
    return messages ? (Array.isArray(messages) ? messages : [messages]) : [];
  });

  if (allMessages.length === 0) {
    toast.error(fallbackMessage);
  } else {
    allMessages.forEach(message => toast.error(getTranslation({message, count, defaultMessage})));
  }

  captureErrorInSentry(error);
};
